import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Navigate } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { useMe } from "@/context/MeContext";
import { useApi, errMsg } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES } from "@/lib/format";
import PlanePhoto from "@/components/PlanePhoto";

const NONE = "none";

function CategorySelect({ value, onChange, testid, className = "" }) {
  return (
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger className={`bg-[#0d0f14] border-white/10 ${className} ${value ? "text-white" : "text-amber-400"}`} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>No category</SelectItem>
        {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Row({ a, onSaved }) {
  const api = useApi();
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(a.name);
  const save = async () => {
    try { await api.patch(`/aircraft/${a.id}`, { name, category: a.category }); toast.success("Renamed"); setEdit(false); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setCategory = async (category) => {
    try { await api.patch(`/aircraft/${a.id}`, { name: a.name, category }); toast.success(`${a.name} category saved`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Remove ${a.name} from the aircraft list? Existing options keep their name.`)) return;
    try { await api.delete(`/aircraft/${a.id}`); toast.success(`${a.name} removed`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const btn = "p-1.5 text-zinc-500 hover:text-[#5B7FD1] transition-colors";
  return (
    <li className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 px-5 py-3" data-testid={`aircraft-row-${a.name}`}>
      {edit ? (
        <>
          <Input className="h-9 bg-[#0d0f14] border-white/10" value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="aircraft-rename-input"
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEdit(false); }} />
          <button className={btn} onClick={save} title="Save" data-testid="aircraft-rename-save"><Check size={15} /></button>
          <button className={btn} onClick={() => { setEdit(false); setName(a.name); }} title="Cancel"><X size={15} /></button>
        </>
      ) : (
        <>
          <span className="w-full sm:w-auto sm:flex-1 min-w-[8rem] text-white text-sm">{a.name}</span>
          <div className="flex gap-2">
            <PlanePhoto aircraft={a} kind="exterior" onSaved={onSaved} />
            <PlanePhoto aircraft={a} kind="interior" onSaved={onSaved} />
          </div>
          <div className="flex items-center gap-1 w-full sm:w-auto">
            <CategorySelect value={a.category} onChange={setCategory} className="h-9 flex-1 sm:flex-none sm:w-44" testid={`aircraft-category-${a.name}`} />
            <button className={btn} onClick={() => setEdit(true)} title="Rename" data-testid={`aircraft-edit-${a.name}`}><Pencil size={13} /></button>
            <button className={btn} onClick={del} title="Remove" data-testid={`aircraft-delete-${a.name}`}><Trash2 size={13} /></button>
          </div>
        </>
      )}
    </li>
  );
}

export default function AircraftList() {
  const { me } = useMe();
  return me?.role === "admin" ? <AircraftManager /> : <Navigate to="/" replace />;
}

function AircraftManager() {
  const api = useApi();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState(null);
  const [missingOnly, setMissingOnly] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/aircraft/all"); setList(data); } catch (e) { toast.error(errMsg(e)); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/aircraft", { name, category }); toast.success(`${data.name} added`); setName(""); setCategory(null); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const missing = (list || []).filter((a) => !a.category).length;
  const shown = (list || []).filter((a) => a.name.toLowerCase().includes(q.trim().toLowerCase()) && (!missingOnly || !a.category));

  return (
    <div data-testid="aircraft-list-page">
      <PageHero testId="aircraft-hero">
        <p className="eyebrow mb-3">Admin</p>
        <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white mb-3">Aircraft list</h1>
        <p className="text-zinc-300 text-sm max-w-xl">These names are suggested as the flight team types an aircraft on an option. Picking one fills in its category automatically. Upload sharper exterior and interior photos for any plane; planes without photos use the category pictures.</p>
      </PageHero>
      <div className="max-w-3xl">
        <form onSubmit={add} className="flex flex-col sm:flex-row gap-3 mb-6">
          <Input className="h-11 bg-[#0d0f14] border-white/10" value={name} onChange={(e) => setName(e.target.value)} placeholder="Add an aircraft, e.g. Gulfstream G650" minLength={2} maxLength={80} required data-testid="aircraft-new-input" />
          <CategorySelect value={category} onChange={setCategory} className="h-11 sm:w-52" testid="aircraft-new-category" />
          <button type="submit" className="frame-btn gold" data-testid="aircraft-add-btn"><Plus size={13} /> Add aircraft</button>
        </form>
        <div className="relative mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input className="pl-9 h-11 bg-[#111319] border-white/10" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${list?.length ?? ""} aircraft`} data-testid="aircraft-search-input" />
        </div>
        {(missing > 0 || missingOnly) && (
          <button type="button" onClick={() => setMissingOnly((v) => !v)} className="mb-4 text-xs text-amber-400 hover:text-amber-300 transition-colors" data-testid="aircraft-missing-filter">
            {missingOnly ? "Show all aircraft" : `${missing} aircraft need a category. Show them`}
          </button>
        )}
        <ul className="lux-card divide-y divide-white/[0.06]" data-testid="aircraft-list">
          {!list && <li className="py-10 grid place-items-center"><div className="jet-loader" /></li>}
          {list && shown.length === 0 && <li className="px-5 py-8 text-sm text-zinc-500">No aircraft match.</li>}
          {shown.map((a) => <Row key={a.id} a={a} onSaved={load} />)}
        </ul>
      </div>
    </div>
  );
}
